// Interim course-"hub" classifier.
//
// Sessions carry a free-text location (a CITY, e.g. "San Diego, CA") and a
// campus string, but the CEO's reports bucket everything into four course hubs:
//
//   WA = Washington   HI = Hawaii   CA = California   VA = Virginia
//
// Power Apps will eventually expose the hub as a first-class field on the
// session/course; until then this module derives it from the location/campus
// text so the revenue + registration reports can group and compare by hub now.
//
// Per the CEO, NOTHING should be left "Unclassified": every location is rolled
// up to the NEAREST hub. We do this via a state -> nearest-hub map covering all
// US states / DC / territories, plus a few explicit keyword rules (online -> CA,
// Guam/Pita -> VA). Only truly empty / unrecognizable text stays null.
//
// Pure (no XLSX / DOM / network) so it can be unit tested.

export const HUBS = {
  WA: 'Washington',
  HI: 'Hawaii',
  CA: 'California',
  VA: 'Virginia'
};

// Canonical hub codes in display order.
export const HUB_CODES = Object.keys(HUBS); // ['WA', 'HI', 'CA', 'VA']

const HUB_CODE_SET = new Set(HUB_CODES);

// Nearest hub for every US state / DC / territory. Buckets:
//   WA  = Pacific NW + northern tier (WA, OR, AK, ID, MT, ND, SD)
//   CA  = California + Mountain West / Southwest (CA, NV, AZ, UT, NM, CO, WY)
//   HI  = Hawaii
//   VA  = everything central + east + south (and Guam per CEO)
export const STATE_HUB = {
  // WA hub
  WA: 'WA', OR: 'WA', AK: 'WA', ID: 'WA', MT: 'WA', ND: 'WA', SD: 'WA',
  // CA hub
  CA: 'CA', NV: 'CA', AZ: 'CA', UT: 'CA', NM: 'CA', CO: 'CA', WY: 'CA',
  // HI hub
  HI: 'HI',
  // VA hub (central + east + south + territories; Guam -> VA per CEO)
  AL: 'VA', AR: 'VA', CT: 'VA', DC: 'VA', DE: 'VA', FL: 'VA', GA: 'VA',
  IA: 'VA', IL: 'VA', IN: 'VA', KS: 'VA', KY: 'VA', LA: 'VA', MA: 'VA',
  MD: 'VA', ME: 'VA', MI: 'VA', MN: 'VA', MO: 'VA', MS: 'VA', NC: 'VA',
  NE: 'VA', NH: 'VA', NJ: 'VA', NY: 'VA', OH: 'VA', OK: 'VA', PA: 'VA',
  RI: 'VA', SC: 'VA', TN: 'VA', TX: 'VA', VA: 'VA', VT: 'VA', WI: 'VA',
  WV: 'VA', PR: 'VA', VI: 'VA', GU: 'VA'
};

// Full state / territory names -> 2-letter code, for locations that spell the
// state out instead of using the "City, ST" suffix.
const STATE_NAME_TO_CODE = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI',
  wyoming: 'WY', 'puerto rico': 'PR', guam: 'GU'
};

// City / keyword cues per hub, applied before the generic state map so that
// unusual location text (no state suffix) still lands in the right hub.
const HUB_KEYWORD_RULES = [
  ['WA', /washington|seattle|tacoma|everett|bellingham|olympia|anacortes|edmonds|burien|\bkent\b|renton|bremerton/i],
  ['HI', /hawaii|honolulu|pearl\s*harbor|\boahu\b/i],
  // Online classes and California cities roll up to the CA hub (per CEO).
  ['CA', /california|san\s*diego|alameda|\bonline\b/i],
  // Guam and Pita are serviced by the VA hub (per CEO). The Maritime East
  // campus is in Virginia, so any otherwise unclassified session on that campus
  // rolls up to the VA hub (per CEO). The campus text arrives as the Dataverse
  // formatted value "Maritime - East" (raw option "maritimeinstitute-east"), so
  // match every spelling: "Maritime - East", "Maritime East", "maritime-east",
  // and "maritimeinstitute-east". West-campus sessions are always placed by
  // their location's state code (a higher-priority rule), so "east" here never
  // catches them.
  ['VA', /virginia|norfolk|guam|\bpita\b|maritime[\s-]*(?:institute[\s-]*)?east/i]
];

// Pull a trailing 2-letter US state code out of a "City, ST" string.
// Returns an upper-cased code (e.g. 'CA') or null.
function trailingStateCode(text) {
  const m = String(text || '').trim().match(/,\s*([A-Za-z]{2})\.?\s*$/);
  return m ? m[1].toUpperCase() : null;
}

// Scan free text for a spelled-out state / territory name; return its code.
function stateCodeFromName(text) {
  const t = String(text || '').toLowerCase();
  for (const [name, code] of Object.entries(STATE_NAME_TO_CODE)) {
    if (new RegExp(`\\b${name}\\b`).test(t)) return code;
  }
  return null;
}

/**
 * Classify a session's location (city) / campus text into a course hub, always
 * rolling up to the NEAREST hub so nothing is left unclassified when it can be
 * geographically placed.
 *
 * @param {string} location free-text location, usually "City, ST"
 * @param {string} [campus] free-text campus / billing string (fallback cues)
 * @returns {'WA'|'HI'|'CA'|'VA'|null} hub code, or null when it can't be placed
 */
export function classifyHub(location, campus = '') {
  const loc = String(location || '').trim();
  const camp = String(campus || '').trim();

  // 1. Most reliable: the state code suffix -> nearest hub.
  const locState = trailingStateCode(loc);
  if (locState && STATE_HUB[locState]) return STATE_HUB[locState];
  const campState = trailingStateCode(camp);
  if (campState && STATE_HUB[campState]) return STATE_HUB[campState];

  // 2. City / keyword cues (location first, then campus).
  const haystack = `${loc} || ${camp}`;
  for (const [code, pattern] of HUB_KEYWORD_RULES) {
    if (pattern.test(haystack)) return code;
  }

  // 3. Bare hub code as the whole value (e.g. location literally "WA").
  const bare = loc.toUpperCase();
  if (HUB_CODE_SET.has(bare)) return bare;

  // 4. Spelled-out state / territory name anywhere in the text -> nearest hub.
  const namedState = stateCodeFromName(haystack);
  if (namedState && STATE_HUB[namedState]) return STATE_HUB[namedState];

  return null;
}

// Full hub display name for a code (e.g. 'CA' -> 'California'), or '' if unknown.
export function hubName(code) {
  return HUBS[code] || '';
}
